// Platform settings (spec/14 §14, F-HQ-38; #455): HQ's defaults for every setting in the registry and
// its locks at platform scope, one organisation's settings with HQ's locks there, and the brand's
// runtime values (`BrandPage.tsx`). All three are pages of one section, under one heading, with the
// same two tabs and the same way to open an organisation.
//
// The settings pages are the venue console's settings screen (`settings/SettingsPage.tsx`) on HQ's
// two resources — `GET/PUT /v1/hq/settings` and `GET/PUT /v1/hq/organizations/{o}/settings` — so a
// setting added to the registry appears here with no HQ code, and the renderer's sources, reset, 412
// panel and locks are the same code. HQ may lock only where the specification marks a setting
// lockable (`lockableAt`); the server refuses any other lock, and the screen offers none.
//
// An organisation's settings are opened from its page on the Organisations screen (spec/14 §3.1), or
// here by its id.

import { useTranslation } from '@clavian/i18n/react';
import { Button, TextField } from '@clavian/ui-web';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../api/problems';
import type { SettingsScope } from '../settings/registry-source';
import { SettingsScreen, useSettingsView } from '../settings/SettingsPage';
import type { SourceWords, ValueSource } from '../settings/types';
import { RequirePlatformPermission } from './access';
import { HQ_PATHS } from './paths';
import { HQ_SETTINGS } from './sections';

const PLATFORM: SettingsScope = { kind: 'platform' };

/** A TypeID: `org_` and 26 base32 characters. The server checks it again (`400`). */
const ORGANIZATION_ID = /^org_[0-9a-z]{26}$/;

/** Where a value comes from, in HQ's words: the platform's default is HQ's own. */
export function useHqSources(screen: 'platform' | 'organization' = 'platform'): SourceWords {
  const { t } = useTranslation('hq');
  return useMemo(
    () => ({
      badge: (source: ValueSource) => t(`settings.source.${source}`),
      name: (source: ValueSource) => t(`settings.sourceName.${source}`),
      using: (source: ValueSource, value: string) => t(`settings.using.${source}`, { value }),
      changed: t(`settings.changed.${screen}`),
      resetTo: (source: ValueSource, value: string) => t(`settings.resetTo.${source}`, { value }),
    }),
    [t, screen],
  );
}

export type PlatformSettingsTab = 'defaults' | 'brand' | 'providers' | 'organization';

/** The section's two tabs, and the field that opens one organisation's settings. */
export function PlatformSettingsHeader({ current, organizationId }: { readonly current: PlatformSettingsTab; readonly organizationId?: string }) {
  const { t } = useTranslation('hq');
  return (
    <div className="console-hq-header">
      <nav className="console-tabs" aria-label={t('settings.sections')}>
        <ul>
          <li>
            <Link to={HQ_PATHS.settings} aria-current={current === 'defaults' ? 'page' : undefined}>
              {t('settings.defaults')}
            </Link>
          </li>
          <li>
            <Link to={HQ_PATHS.brand} aria-current={current === 'brand' ? 'page' : undefined}>
              {t('settings.brand')}
            </Link>
          </li>
          <li>
            <Link to={HQ_PATHS.providers} aria-current={current === 'providers' ? 'page' : undefined}>
              {t('settings.providers')}
            </Link>
          </li>
        </ul>
      </nav>
      <OpenOrganization {...(organizationId === undefined ? {} : { current: organizationId })} />
    </div>
  );
}

function OpenOrganization({ current }: { readonly current?: string }) {
  const { t } = useTranslation('hq');
  const navigate = useNavigate();
  const [id, setId] = useState(current ?? '');
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <form
      className="console-hq-open"
      aria-label={t('settings.organization')}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const organizationId = id.trim().toLowerCase();
        if (!ORGANIZATION_ID.test(organizationId)) {
          setError(t('settings.open.invalid'));
          return;
        }
        setError(undefined);
        const params: Readonly<Record<string, string>> = { organizationId };
        void navigate({ to: HQ_PATHS.organizationSettings, params });
      }}
    >
      <TextField
        className="console-hq-open__id"
        label={t('settings.open.label')}
        value={id}
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => setId(event.target.value)}
        {...(error === undefined ? {} : { error })}
      />
      <Button type="submit" variant="secondary">
        {t('settings.open.submit')}
      </Button>
    </form>
  );
}

/** `/settings` and `/settings/$group`: every setting in the registry at platform scope, and its lock. */
export function PlatformSettingsPage(): ReactNode {
  const { t } = useTranslation('hq');
  const params = useParams({ strict: false }) as { group?: string };
  const sources = useHqSources();
  return (
    <RequirePlatformPermission permission={HQ_SETTINGS}>
      <SettingsScreen
        scope={PLATFORM}
        group={params.group}
        groupLink={(group) => ({ to: HQ_PATHS.settingsGroup, params: { group } })}
        heading={t('settings.title')}
        header={<PlatformSettingsHeader current="defaults" />}
        sources={sources}
        notSetHere={t('settings.notHere.platform')}
      />
    </RequirePlatformPermission>
  );
}

/** `/organizations/$organizationId/settings[/$group]`: one organisation's, set or locked by HQ. */
export function HqOrganizationSettingsPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_SETTINGS}>
      <OrganizationSettings />
    </RequirePlatformPermission>
  );
}

function OrganizationSettings() {
  const { t } = useTranslation('hq');
  const params = useParams({ strict: false }) as { organizationId?: string; group?: string };
  const organizationId = params.organizationId ?? '';
  const scope = useMemo<SettingsScope>(() => ({ kind: 'hqOrganization', organizationId }), [organizationId]);
  const sources = useHqSources('organization');
  const { query } = useSettingsView(scope);

  const header = (
    <>
      <PlatformSettingsHeader current="organization" organizationId={organizationId} />
      <p>
        {t('settings.organizationTitle')} <span className="console-hq-subject">{organizationId}</span>
      </p>
      <p className="console-muted">{t('settings.audited')}</p>
    </>
  );

  // No such organisation, or an id the api does not take: said as that, not as a failure to retry.
  const code = problemCode(query.error);
  if (query.data === undefined && (code === PROBLEM.notFound || code === PROBLEM.validationFailed)) {
    return (
      <div className="console-page">
        <h1 className="console-page__title">{t('settings.title')}</h1>
        {header}
        <p role="alert">{t('settings.noSuchOrganization')}</p>
      </div>
    );
  }

  return (
    <SettingsScreen
      scope={scope}
      group={params.group}
      groupLink={(group) => ({ to: HQ_PATHS.organizationSettingsGroup, params: { organizationId, group } })}
      heading={t('settings.title')}
      header={header}
      sources={sources}
      notSetHere={t('settings.notHere.organization')}
    />
  );
}
